import React, { useEffect, useRef, useState } from 'react';
import Logo from '../../../assets/logo.png';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../stores/global-settings-branches';
import globalConfigStore from '../../../stores/global-config-store';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import { Link } from 'react-router-dom';
import { SideBarUserProfile } from './components/side-bar-user-profile';

const SIDEBAR_WIDTH_EXPANDED = 254;
const SIDEBAR_WIDTH_COLLAPSED = 72;
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'adminSidebarCollapsed';

type MenuItem = { icon: string; label: string; route: string };

type ReportGroup = {
  label: string;
  icon: string;
  items: MenuItem[];
  category: string;
};

const CollapsedIconLink: React.FC<{
  to: string;
  icon: string;
  tooltip: string;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}> = ({ to, icon, tooltip, onClick }) => (
  <div
    className="menu-item collapsed-icon-item"
    onClick={onClick}
    data-pr-tooltip={tooltip}
    data-pr-position="left"
  >
    <Link
      to={to}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        color: 'inherit',
        width: '100%',
        height: '100%',
      }}
    >
      <i className={`pi ${icon}`} />
    </Link>
  </div>
);

const CollapsedFlyoutButton: React.FC<{
  icon: string;
  tooltip: string;
  items: MenuItem[];
  category?: string;
}> = ({ icon, tooltip, items, category }) => {
  const overlayRef = useRef<OverlayPanel>(null);

  return (
    <>
      <div
        className="menu-item collapsed-icon-item"
        onClick={(e) => overlayRef.current?.toggle(e)}
        data-pr-tooltip={tooltip}
        data-pr-position="left"
      >
        <i className={`pi ${icon}`} />
      </div>
      <OverlayPanel
        ref={overlayRef}
        className="sidebar-flyout-panel"
        style={{ direction: 'rtl', minWidth: '220px' }}
      >
        {items.map((item, index) => (
          <Link
            key={index}
            to={item.route}
            style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}
            onClick={() => overlayRef.current?.hide()}
          >
            <div className="menu-item flyout-item" data-category={category}>
              <i
                className={`pi ${item.icon}`}
                style={{ marginLeft: '0.5rem' }}
              />
              <span>{item.label}</span>
            </div>
          </Link>
        ))}
      </OverlayPanel>
    </>
  );
};

const CollapsedReportsFlyout: React.FC<{
  groups: ReportGroup[];
}> = ({ groups }) => {
  const overlayRef = useRef<OverlayPanel>(null);

  return (
    <>
      <div
        className="menu-item collapsed-icon-item"
        onClick={(e) => overlayRef.current?.toggle(e)}
        data-pr-tooltip="דוחות"
        data-pr-position="left"
      >
        <i className="pi pi-chart-bar" />
      </div>
      <OverlayPanel
        ref={overlayRef}
        className="sidebar-flyout-panel"
        style={{ direction: 'rtl', minWidth: '260px' }}
      >
        {groups.map((group) => (
          <div key={group.label} className="flyout-group">
            <div className="flyout-group-header">
              <i
                className={`pi ${group.icon}`}
                style={{ marginLeft: '0.5rem' }}
              />
              <span>{group.label}</span>
            </div>
            {group.items.map((item, index) => (
              <Link
                key={index}
                to={item.route}
                style={{
                  textDecoration: 'none',
                  color: 'inherit',
                  width: '100%',
                }}
                onClick={() => overlayRef.current?.hide()}
              >
                <div
                  className="menu-item flyout-item sub-flyout-item"
                  data-category={group.category}
                >
                  <i
                    className={`pi ${item.icon}`}
                    style={{ marginLeft: '0.5rem' }}
                  />
                  <span>{item.label}</span>
                </div>
              </Link>
            ))}
          </div>
        ))}
      </OverlayPanel>
    </>
  );
};

const SideBarAdministration: React.FC = () => {
  const [expanded1, setExpanded1] = useState<boolean>(false);
  const [expanded2, setExpanded2] = useState<boolean>(false);
  const [expandedReports, setExpandedReports] = useState<boolean>(false);
  const [expandedStudiesReports, setExpandedStudiesReports] =
    useState<boolean>(false);
  const [expandedFinanceReports, setExpandedFinanceReports] =
    useState<boolean>(false);
  const [expandedAccountingReports, setExpandedAccountingReports] =
    useState<boolean>(false);
  const [expandedManagementReports, setExpandedManagementReports] =
    useState<boolean>(false);
  const [expandedSettings, setExpandedSettings] = useState<boolean>(false);
  const [expandedUsers, setExpandedUsers] = useState<boolean>(false);
  const [expandedAccounting, setExpandedAccounting] = useState<boolean>(false);
  const [expandedPayments, setExpandedPayments] = useState<boolean>(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  });

  const sidebarWidth = isCollapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : SIDEBAR_WIDTH_EXPANDED;

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  };

  // Add years array for the current decade
  const currentYear = new Date().getFullYear();
  const workingManagementYear =
    globalSettingsBranches.workingTimeManagement?.currentWorkingYear;
  const defaultWorkingYear = workingManagementYear ?? currentYear;

  useEffect(() => {
    if (
      globalConfigStore.rootFilter.currentYear === undefined ||
      (workingManagementYear !== undefined &&
        globalConfigStore.rootFilter.currentYear === currentYear)
    ) {
      globalConfigStore.rootFilter.currentYear = defaultWorkingYear;
    }
  }, [currentYear, defaultWorkingYear, workingManagementYear]);

  const years = Array.from(new Array(5), (val, index) => {
    const startYear = currentYear - 1 + index;
    const endYear = startYear + 1;
    return {
      label: `${startYear} - ${endYear}`,
      value: startYear,
    };
  });

  const handleBranchChange = (branchId: number) => {
    const selectedBranch = globalSettingsBranches.branches.find(
      (b) => b.id === branchId,
    );
    if (selectedBranch) {
      globalConfigStore.rootFilter.currentBranches = selectedBranch;
    }
  };

  const handleYearChange = (year: number) => {
    globalConfigStore.rootFilter.currentYear = year;
  };

  const createRipple = (event: React.MouseEvent<HTMLDivElement>) => {
    const button = event.currentTarget;
    const ripple = document.createElement('span');
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.classList.add('ripple');

    const existingRipple = button.getElementsByClassName('ripple')[0];
    if (existingRipple) {
      existingRipple.remove();
    }

    button.appendChild(ripple);

    ripple.addEventListener('animationend', () => {
      ripple.remove();
    });
  };

  const ExpandableSection = ({
    expanded,
    setExpanded,
    title,
    icon,
    children,
  }: {
    expanded: boolean;
    setExpanded: (value: boolean) => void;
    title: string;
    icon?: string;
    children?: React.ReactNode;
  }) => (
    <div className="expandable">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: '45px',
          width: '100%',
          padding: '0 16px',
          cursor: 'pointer',
        }}
        className="expandable-header"
        onClick={() => setExpanded(!expanded)}
      >
        {icon && (
          <i
            className={`pi ${icon}`}
            style={{
              marginLeft: '0.5rem',
              color: 'var(--surface-600)',
              fontSize: '0.9rem',
            }}
          />
        )}
        <span
          style={{
            color: 'var(--surface-600)',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {title}
        </span>
        <div style={{ flex: 1 }} />

        <i
          className="pi pi-chevron-down"
          style={{
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            color: 'var(--surface-600)',
            fontSize: '0.8rem',
          }}
        />
      </div>
      <div
        className="expandable-content"
        style={{
          maxHeight: expanded ? '1000px' : '0',
          opacity: expanded ? '1' : '0',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: `translateY(${expanded ? '0' : '-10px'})`,
        }}
      >
        {children}
      </div>
    </div>
  );

  const ExpandableItem = ({
    expanded,
    setExpanded,
    label,
    icon,
    items,
    category,
  }: {
    expanded: boolean;
    setExpanded: (value: boolean) => void;
    label: string;
    icon: string;
    items: Array<{ icon: string; label: string; route: string }>;
    category: string;
  }) => (
    <div>
      <div className="menu-item" onClick={() => setExpanded(!expanded)}>
        <i className={`pi ${icon}`} style={{ marginLeft: '0.5rem' }}></i>
        <span>{label}</span>
        <div style={{ flex: 1 }} />
        <i
          className="pi pi-chevron-down"
          style={{
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            color: 'var(--surface-600)',
            fontSize: '0.8rem',
          }}
        />
      </div>
      <div
        className="expandable-content"
        style={{
          maxHeight: expanded ? `${items.length * 45}px` : '0',
          opacity: expanded ? '1' : '0',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: `translateY(${expanded ? '0' : '-10px'})`,
        }}
      >
        <div className="menu-items">
          {items.map((item, index) => (
            <Link
              key={index}
              to={item.route}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                width: '100%',
              }}
            >
              <div
                className="menu-item sub-item"
                data-category={category}
                onClick={createRipple}
                style={{
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  opacity: expanded ? '1' : '0',
                  transform: `translateX(${expanded ? '0' : '-10px'})`,
                  transitionDelay: `${index * 0.05}s`,
                }}
              >
                <i
                  className={`pi ${item.icon}`}
                  style={{ marginLeft: '0.5rem' }}
                ></i>
                <span>{item.label}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  const studiesItems = [
    { icon: 'pi-book', label: 'תוכניות לימודים', route: '/programs' },
    { icon: 'pi-list', label: 'קורסים', route: '/courses-list' },
    { icon: 'pi-calendar', label: 'מפגשים', route: '/education/lectures' },
    {
      icon: 'pi-file-edit',
      label: 'מבחנים חיצוניים',
      route: '/external-exams',
    },
  ];

  const paymentItems = [
    {
      icon: 'pi-credit-card',
      label: 'שירותי תשלום',
      route: '/payment-services',
    },
  ];

  const crmItems = [
    {
      icon: 'pi-building',
      label: 'חשבונות',
      route: '/crm/accounts',
    },
    {
      icon: 'pi-wallet',
      label: 'תשלומי חשבונות',
      route: '/crm/accounts-payments',
    },
  ];

  const studiesReportsItems = [
    {
      icon: 'pi-calendar-times',
      label: 'נוכחות כללית',
      route: '/reports/education/attendance',
    },
    {
      icon: 'pi-users',
      label: 'דוח נושרים',
      route: '/reports/education/dropout',
    },
    { icon: 'pi-file', label: 'מבחנים', route: '/reports/education/exams' },
    {
      icon: 'pi-check-square',
      label: 'מטלות',
      route: '/reports/education/assignments',
    },
    {
      icon: 'pi-calendar',
      label: 'מפגשים חורגים',
      route: '/reports/education/lectures',
    },
    {
      icon: 'pi-chart-bar',
      label: 'סטטוס סטודנטים בקורס',
      route: '/reports/education/students-course-status',
    },
    {
      icon: 'pi-comments',
      label: 'משובי הוראה',
      route: '/reports/education/teaching-feedback',
    },
  ];

  const financeReportsItems = [
    {
      icon: 'pi-clock',
      label: 'חריגת שעות הוראה בקורסים',
      route: '/reports/finance/courses-exceeding-hours',
    },
    {
      icon: 'pi-calendar-plus',
      label: 'עלות ומשך קורס בשלוחות',
      route: '/reports/finance/courses-duration-expanded',
    },
    {
      icon: 'pi-building',
      label: 'ניצול נכסי נדלן',
      route: '/reports/finance/real-estate-comparison',
    },
    {
      icon: 'pi-money-bill',
      label: 'שכר לימוד',
      route: '/reports/finance/students-financial',
    },
    {
      icon: 'pi-users',
      label: 'הכנסות מלקוחות',
      route: '/reports/finance/crm-payments',
    },
    {
      icon: 'pi-chart-line',
      label: 'פירוט פיננסי של קורסים לפי שלוחה',
      route: '/reports/finance/course-financial-branch-breakdown',
    },
    {
      icon: 'pi-exchange',
      label: 'השוואה פיננסית בין תוכניות',
      route: '/reports/finance/program-revenue-between-branches',
    },
    /*    {
      icon: 'pi-file-o',
      label: 'דוח אלמנטרי',
      route: '/reports/finance/elementary',
    },*/
  ];

  const accountingReportsItems = [
    {
      icon: 'pi-wallet',
      label: 'תשלומי שכ"ל – כללי',
      route: '/reports/tuition-payments',
    },
    { icon: 'pi-ticket', label: 'המחאות', route: '/reports/checks' },
    {
      icon: 'pi-ticket',
      label: 'המחאות - לקוחות',
      route: '/reports/accountant/crm-checks',
    },
    {
      icon: 'pi-credit-card',
      label: 'הוראות קבע באשראי',
      route: '/reports/standing-orders',
    },
    {
      icon: 'pi-sync',
      label: 'הוראות קבע בנקאיות',
      route: '/reports/bank-transfer-installments',
    },
    {
      icon: 'pi-gift',
      label: 'מלגות',
      route: '/reports/scholarships',
    },
  ];

  const managementReportsItems = [
    {
      icon: 'pi-percentage',
      label: 'תעריפי הוראה',
      route: '/reports/management/rates',
    },
    {
      icon: 'pi-credit-card',
      label: 'דו״ח סליקות אשראי',
      route: '/reports/management/credit-card-clarence',
    },
  ];

  const accountingItems = [
    {
      icon: 'pi-money-bill',
      label: 'מחולל משכורות',
      route: '/accounting/salaries',
    },
    {
      icon: 'pi-money-bill',
      label: 'מחולל משכורות',
      route: '/accounting/salaries/flat',
    },
    {
      icon: 'pi-history',
      label: 'ייצוא לשכר',
      route: '/accounting/salaries/history',
    },
    {
      icon: 'pi-heart',
      label: 'תרומות',
      route: '/accounting/donations',
    },
    {
      icon: 'pi-receipt',
      label: 'מסמכים חשבונאיים',
      route: '/accounting-documents',
    },
    {
      icon: 'pi-book',
      label: 'פקודות יומן',
      route: '/journal-entries',
    },
    {
      icon: 'pi-database',
      label: 'דו״ח הפקדות',
      route: '/accounting/deposit',
    },
  ];

  const settingsItems = [
    { icon: 'pi-book', label: 'לימודים', route: '/settings/studies' },
    { icon: 'pi-users', label: 'מרצים', route: '/settings/lectures' },
    { icon: 'pi-cog', label: 'שרותים', route: '/settings/operation' },
    { icon: 'pi-file', label: 'מסמכים', route: '/settings/docs' },
    { icon: 'pi-calendar', label: 'שנות עבודה', route: '/settings/times' },
    {
      icon: 'pi-building',
      label: 'כיתות ושלוחות',
      route: '/settings/branch-and-classes',
    },
    {
      icon: 'pi-comments',
      label: 'משובים למרצים',
      route: '/settings/professor-feedback',
    },
    {
      icon: 'pi-lock',
      label: 'הרשאות משתמשים',
      route: '/settings/permissions-groups',
    },
    {
      icon: 'pi-wallet',
      label: 'הנהלת חשבונות',
      route: '/accountant',
    },
  ];

  const usersItems = [
    { icon: 'pi-graduation-cap', label: 'סטודנטים', route: '/students' },
    { icon: 'pi-user-edit', label: 'מרצים', route: '/lecturers' },
    { icon: 'pi-users', label: 'מנהלה', route: '/administration' },
  ];

  const reportGroups: ReportGroup[] = [
    {
      label: 'לימודים',
      icon: 'pi-book',
      items: studiesReportsItems,
      category: 'education',
    },
    {
      label: 'פיננסים',
      icon: 'pi-money-bill',
      items: financeReportsItems,
      category: 'finance',
    },
    {
      label: 'הנהלת חשבונות',
      icon: 'pi-wallet',
      items: accountingReportsItems,
      category: 'accounting',
    },
    {
      label: 'ניהוליים',
      icon: 'pi-briefcase',
      items: managementReportsItems,
      category: 'management',
    },
  ];

  const renderCollapsedMenu = () => (
    <div className="collapsed-menu">
      <CollapsedIconLink
        to="/home"
        icon="pi-home"
        tooltip="דף הבית"
        onClick={createRipple}
      />
      <CollapsedIconLink
        to="/"
        icon="pi-chart-line"
        tooltip="דף הבית - הנהלה"
        onClick={createRipple}
      />
      <div style={{ height: '8px' }} />
      <CollapsedFlyoutButton
        icon="pi-book"
        tooltip="לימודים"
        items={studiesItems}
        category="education"
      />
      <CollapsedFlyoutButton
        icon="pi-credit-card"
        tooltip="תשלומים"
        items={paymentItems}
        category="finance"
      />
      <CollapsedFlyoutButton
        icon="pi-building"
        tooltip="ניהול לקוחות"
        items={crmItems}
        category="crm"
      />
      <CollapsedFlyoutButton
        icon="pi-users"
        tooltip="משתמשים"
        items={usersItems}
      />
      <CollapsedFlyoutButton
        icon="pi-wallet"
        tooltip="הנהלת חשבונות"
        items={accountingItems}
        category="accounting"
      />
      <CollapsedReportsFlyout groups={reportGroups} />
      <CollapsedFlyoutButton
        icon="pi-cog"
        tooltip="הגדרות"
        items={settingsItems}
      />
    </div>
  );

  return (
    <div
      className={isCollapsed ? 'sidebar-collapsed' : ''}
      style={{
        width: `${sidebarWidth}px`,
        marginLeft: '16px',
        minWidth: `${sidebarWidth}px`,
        maxWidth: `${sidebarWidth}px`,
        direction: 'rtl',
        height: '100vh',
        backgroundColor: '#ffffff',
        position: 'sticky',
        top: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '1px 0 2px rgba(0, 0, 0, 0.05)',
        zIndex: 1000,
        borderLeft: '1px solid var(--surface-200)',
        transition:
          'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Tooltip target="[data-pr-tooltip]" />
      <div
        style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: isCollapsed ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            padding: isCollapsed ? '12px 8px' : '16px',
            borderBottom: '1px solid var(--surface-200)',
            backgroundColor: '#ffffff',
            gap: '8px',
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: isCollapsed ? '0' : '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              flexShrink: 0,
            }}
          >
            <img
              src={Logo}
              alt="לימודים אקדמים"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </div>
          {!isCollapsed && (
            <span
              style={{
                fontFamily: "'Heebo', sans-serif",
                fontSize: '0.95rem',
                fontWeight: 500,
                position: 'relative',
                animation: 'shimmerRightToLeft 4s ease-in-out infinite',
                opacity: 0.9,
                flex: 1,
              }}
            >
              המרכז החרדי
            </span>
          )}
          <button
            type="button"
            className="sidebar-collapse-toggle"
            onClick={toggleCollapsed}
            data-pr-tooltip={isCollapsed ? 'הרחב תפריט' : 'כווץ תפריט'}
            data-pr-position="left"
            aria-label={isCollapsed ? 'הרחב תפריט' : 'כווץ תפריט'}
          >
            <i
              className={`pi ${isCollapsed ? 'pi-angle-double-left' : 'pi-angle-double-right'}`}
            />
          </button>
        </div>
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            paddingRight: isCollapsed ? '8px' : '16px',
            paddingLeft: isCollapsed ? '8px' : '16px',
          }}
        >
          {isCollapsed ? (
            renderCollapsedMenu()
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  padding: '16px',
                  backgroundColor: 'var(--surface-50)',
                  marginBottom: '16px',
                  borderRadius: '8px',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '4px',
                    }}
                  >
                    <i
                      className="pi pi-building"
                      style={{
                        marginLeft: '8px',
                        color: 'var(--surface-700)',
                        fontSize: '0.9rem',
                      }}
                    />
                    <span
                      style={{
                        color: 'var(--surface-700)',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      שלוחה נוכחית
                    </span>
                  </div>
                  <Dropdown
                    value={globalConfigStore.rootFilter.currentBranches?.id}
                    options={globalSettingsBranches.branches}
                    onChange={(e) => handleBranchChange(e.value)}
                    optionLabel="branchName"
                    optionValue="id"
                    placeholder="בחר שלוחה"
                    className="branch-dropdown"
                    style={{
                      width: '100%',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      border: '1px solid var(--surface-200)',
                      borderRadius: '6px',
                    }}
                  />
                </div>
                <div
                  style={{
                    height: '1px',
                    backgroundColor: 'var(--surface-200)',
                    margin: '4px 0',
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '4px',
                    }}
                  >
                    <i
                      className="pi pi-calendar"
                      style={{
                        marginLeft: '8px',
                        color: 'var(--surface-700)',
                        fontSize: '0.9rem',
                      }}
                    />
                    <span
                      style={{
                        color: 'var(--surface-700)',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                      }}
                    >
                      שנת לימודים נוכחית
                    </span>
                  </div>
                  <Dropdown
                    value={
                      globalConfigStore.rootFilter.currentYear ??
                      defaultWorkingYear
                    }
                    options={years}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => handleYearChange(e.value)}
                    placeholder="בחר שנה"
                    className="year-dropdown"
                    style={{
                      width: '100%',
                      fontSize: '0.875rem',
                      backgroundColor: 'white',
                      border: '1px solid var(--surface-200)',
                      borderRadius: '6px',
                    }}
                  />
                </div>
              </div>
              <div
                className="menu-item"
                onClick={createRipple}
                style={{
                  height: '45px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Link
                  to="/home"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    width: '100%',
                  }}
                >
                  <i
                    className="pi pi-home"
                    style={{ marginLeft: '0.5rem' }}
                  ></i>
                  <span>דף הבית</span>
                </Link>
              </div>

              <div
                className="menu-item"
                onClick={createRipple}
                style={{
                  height: '45px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Link
                  /*to="/reports/high-level"*/
                  to="/"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    width: '100%',
                  }}
                >
                  <i
                    className="pi pi-chart-line"
                    style={{ marginLeft: '0.5rem' }}
                  ></i>
                  <span>דף הבית - הנהלה</span>
                </Link>
              </div>

              <div style={{ height: '24px' }} />
              <ExpandableSection
                expanded={expanded2}
                setExpanded={setExpanded2}
                title="לימודים"
                icon="pi-book"
              >
                <div className="menu-items">
                  {studiesItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.route}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        width: '100%',
                      }}
                    >
                      <div
                        className="menu-item"
                        data-category="education"
                        onClick={createRipple}
                        style={{
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          opacity: expanded2 ? '1' : '0',
                          transform: `translateX(${expanded2 ? '0' : '-10px'})`,
                          transitionDelay: `${index * 0.05}s`,
                        }}
                      >
                        <i
                          className={`pi ${item.icon}`}
                          style={{ marginLeft: '0.5rem' }}
                        ></i>
                        <span>{item.label}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </ExpandableSection>
              <div style={{ height: '24px' }} />
              <ExpandableSection
                expanded={expandedPayments}
                setExpanded={setExpandedPayments}
                title="תשלומים"
                icon="pi-credit-card"
              >
                <div className="menu-items">
                  {paymentItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.route}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        width: '100%',
                      }}
                    >
                      <div
                        className="menu-item"
                        data-category="finance"
                        onClick={createRipple}
                        style={{
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          opacity: expandedPayments ? '1' : '0',
                          transform: `translateX(${expandedPayments ? '0' : '-10px'})`,
                          transitionDelay: `${index * 0.05}s`,
                        }}
                      >
                        <i
                          className={`pi ${item.icon}`}
                          style={{ marginLeft: '0.5rem' }}
                        ></i>
                        <span>{item.label}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </ExpandableSection>
              <div style={{ height: '24px' }} />
              <ExpandableSection
                expanded={expanded1}
                setExpanded={setExpanded1}
                title="ניהול לקוחות"
                icon="pi-building"
              >
                <div className="menu-items">
                  {crmItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.route}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        width: '100%',
                      }}
                    >
                      <div
                        className="menu-item"
                        data-category="crm"
                        onClick={createRipple}
                        style={{
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          opacity: expanded1 ? '1' : '0',
                          transform: `translateX(${expanded1 ? '0' : '-10px'})`,
                          transitionDelay: `${index * 0.05}s`,
                        }}
                      >
                        <i
                          className={`pi ${item.icon}`}
                          style={{ marginLeft: '0.5rem' }}
                        ></i>
                        <span>{item.label}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </ExpandableSection>
              <div style={{ height: '24px' }} />
              <ExpandableSection
                expanded={expandedUsers}
                setExpanded={setExpandedUsers}
                title="משתמשים"
                icon="pi-users"
              >
                <div className="menu-items">
                  {usersItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.route}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        width: '100%',
                      }}
                    >
                      <div
                        className="menu-item"
                        onClick={createRipple}
                        style={{
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          opacity: expandedUsers ? '1' : '0',
                          transform: `translateX(${expandedUsers ? '0' : '-10px'})`,
                          transitionDelay: `${index * 0.05}s`,
                        }}
                      >
                        <i
                          className={`pi ${item.icon}`}
                          style={{ marginLeft: '0.5rem' }}
                        ></i>
                        <span>{item.label}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </ExpandableSection>
              <div style={{ height: '24px' }} />
              <ExpandableSection
                expanded={expandedAccounting}
                setExpanded={setExpandedAccounting}
                title="הנהלת חשבונות"
                icon="pi-wallet"
              >
                <div className="menu-items">
                  {accountingItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.route}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        width: '100%',
                      }}
                    >
                      <div
                        className="menu-item"
                        data-category="accounting"
                        onClick={createRipple}
                        style={{
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          opacity: expandedAccounting ? '1' : '0',
                          transform: `translateX(${expandedAccounting ? '0' : '-10px'})`,
                          transitionDelay: `${index * 0.05}s`,
                        }}
                      >
                        <i
                          className={`pi ${item.icon}`}
                          style={{ marginLeft: '0.5rem' }}
                        ></i>
                        <span>{item.label}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </ExpandableSection>
              <div style={{ height: '24px' }} />
              <ExpandableSection
                expanded={expandedReports}
                setExpanded={setExpandedReports}
                title="דוחות"
                icon="pi-chart-bar"
              >
                <div className="menu-items">
                  <ExpandableItem
                    expanded={expandedStudiesReports}
                    setExpanded={setExpandedStudiesReports}
                    label="לימודים"
                    icon="pi-book"
                    items={studiesReportsItems}
                    category="education"
                  />
                  <ExpandableItem
                    expanded={expandedFinanceReports}
                    setExpanded={setExpandedFinanceReports}
                    label="פיננסים"
                    icon="pi-money-bill"
                    items={financeReportsItems}
                    category="finance"
                  />
                  <ExpandableItem
                    expanded={expandedAccountingReports}
                    setExpanded={setExpandedAccountingReports}
                    label="הנהלת חשבונות"
                    icon="pi-wallet"
                    items={accountingReportsItems}
                    category="accounting"
                  />
                  <ExpandableItem
                    expanded={expandedManagementReports}
                    setExpanded={setExpandedManagementReports}
                    label="ניהוליים"
                    icon="pi-briefcase"
                    items={managementReportsItems}
                    category="management"
                  />
                </div>
              </ExpandableSection>
              <div style={{ height: '24px' }} />
              <ExpandableSection
                expanded={expandedSettings}
                setExpanded={setExpandedSettings}
                title="הגדרות"
                icon="pi-cog"
              >
                <div className="menu-items">
                  {settingsItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.route}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        width: '100%',
                      }}
                    >
                      <div
                        className="menu-item"
                        onClick={createRipple}
                        style={{
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          opacity: expandedSettings ? '1' : '0',
                          transform: `translateX(${expandedSettings ? '0' : '-10px'})`,
                          transitionDelay: `${index * 0.05}s`,
                        }}
                      >
                        <i
                          className={`pi ${item.icon}`}
                          style={{ marginLeft: '0.5rem' }}
                        ></i>
                        <span>{item.label}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </ExpandableSection>
              <div style={{ height: '24px' }} />
            </>
          )}
        </div>
        <SideBarUserProfile collapsed={isCollapsed} />
      </div>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;700&display=swap');

          @keyframes shimmerRightToLeft {
            0% {
              color: var(--surface-600);
            }
            50% {
              color: var(--surface-500);
            }
            100% {
              color: var(--surface-600);
            }
          }
          /* Custom Scrollbar Styling */
          ::-webkit-scrollbar {
            width: 8px;
            background-color: transparent;
          }

          ::-webkit-scrollbar-thumb {
            background-color: var(--surface-300);
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background-color: var(--surface-400);
          }

          ::-webkit-scrollbar-track {
            background-color: transparent;
          }

          /* Hide scrollbar for Firefox */
          * {
            scrollbar-width: thin;
            scrollbar-color: var(--surface-300) transparent;
          }

          .expandable {
            width: 100%;
          }

          .expandable-header {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: none;
            border: none;
            cursor: pointer;
            font-size: 1rem;
            text-align: right;
          }

          .expandable-header:focus {
            outline: none;
          }

          .menu-items {
            padding: 0;
          }

          .menu-item {
            display: flex;
            align-items: center;
            padding: 0px 16px;
            cursor: pointer;
            transition: all 0.2s ease;
            position: relative;
            overflow: hidden;
            height: 45px;
            border-radius: 4px;
          }

          .menu-item.sub-item {
            padding-right: 8px;
            margin-right: 1.5rem;
            height: 45px;
            border-radius: 4px;
          }

          /* Education section (blue) */
          .menu-item[data-category="education"] {
            background-color: rgba(33, 150, 243, 0.03);
          }

          .menu-item[data-category="education"] i,
          .menu-item[data-category="education"] span {
            color: #1976d2;
          }

          .menu-item[data-category="education"]:hover {
            background-color: rgba(33, 150, 243, 0.1);
            box-shadow: 0 2px 4px rgba(33, 150, 243, 0.1);
          }

          .menu-item[data-category="education"]:hover i,
          .menu-item[data-category="education"]:hover span {
            color: #2196f3;
          }

          /* Finance section (green) */
          .menu-item[data-category="finance"] {
            background-color: rgba(76, 175, 80, 0.03);
          }

          .menu-item[data-category="finance"] i,
          .menu-item[data-category="finance"] span {
            color: #388e3c;
          }

          .menu-item[data-category="finance"]:hover {
            background-color: rgba(76, 175, 80, 0.1);
            box-shadow: 0 2px 4px rgba(76, 175, 80, 0.1);
          }

          .menu-item[data-category="finance"]:hover i,
          .menu-item[data-category="finance"]:hover span {
            color: #4caf50;
          }

          /* Accounting section (purple) */
          .menu-item[data-category="accounting"] {
            background-color: rgba(156, 39, 176, 0.03);
          }

          .menu-item[data-category="accounting"] i,
          .menu-item[data-category="accounting"] span {
            color: #7b1fa2;
          }

          .menu-item[data-category="accounting"]:hover {
            background-color: rgba(156, 39, 176, 0.1);
            box-shadow: 0 2px 4px rgba(156, 39, 176, 0.1);
          }

          .menu-item[data-category="accounting"]:hover i,
          .menu-item[data-category="accounting"]:hover span {
            color: #9c27b0;
          }

          /* Management section (orange) */
          .menu-item[data-category="management"] {
            background-color: rgba(255, 152, 0, 0.03);
          }

          .menu-item[data-category="management"] i,
          .menu-item[data-category="management"] span {
            color: #ef6c00;
          }

          .menu-item[data-category="management"]:hover {
            background-color: rgba(255, 152, 0, 0.1);
            box-shadow: 0 2px 4px rgba(255, 152, 0, 0.1);
          }

          .menu-item[data-category="management"]:hover i,
          .menu-item[data-category="management"]:hover span {
            color: #ff9800;
          }

          /* CRM section (teal) */
          .menu-item[data-category="crm"] {
            background-color: rgba(0, 150, 136, 0.03);
          }

          .menu-item[data-category="crm"] i,
          .menu-item[data-category="crm"] span {
            color: #00796b;
          }

          .menu-item[data-category="crm"]:hover {
            background-color: rgba(0, 150, 136, 0.1);
            box-shadow: 0 2px 4px rgba(0, 150, 136, 0.1);
          }

          .menu-item[data-category="crm"]:hover i,
          .menu-item[data-category="crm"]:hover span {
            color: #009688;
          }

          /* Default hover for other items */
          .menu-item:hover {
            background-color: var(--surface-100);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            transform: translateY(-1px);
          }

          .menu-item:hover i,
          .menu-item:hover span {
            color: var(--surface-900);
            text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
          }

          .menu-item i {
            transition: all 0.2s ease;
          }

          .menu-item span {
            transition: all 0.2s ease;
          }

          .menu-item::after {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(255, 255, 255, 0.2),
              transparent
            );
            transition: 0.5s;
          }

          .menu-item:hover::after {
            left: 100%;
          }

          .ripple {
            position: absolute;
            border-radius: 50%;
            transform: scale(0);
            animation: ripple 0.6s cubic-bezier(0.4, 0, 0.2, 1);
            background-color: rgba(0, 0, 0, 0.1);
            pointer-events: none;
          }

          @keyframes ripple {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }

          .branch-dropdown .p-dropdown-label,
          .year-dropdown .p-dropdown-label {
            padding: 8px 12px;
            color: var(--surface-900);
          }

          .branch-dropdown .p-dropdown-trigger,
          .year-dropdown .p-dropdown-trigger {
            padding: 0 8px;
            color: var(--surface-600);
          }

          .branch-dropdown:hover,
          .year-dropdown:hover {
            border-color: var(--surface-400) !important;
          }

          .branch-dropdown:focus,
          .year-dropdown:focus {
            box-shadow: 0 0 0 2px var(--surface-100) !important;
            border-color: var(--surface-500) !important;
          }

          .branch-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item,
          .year-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item {
            padding: 8px 12px;
            color: var(--surface-700);
            font-size: 0.875rem;
          }

          .branch-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover,
          .year-dropdown .p-dropdown-panel .p-dropdown-items .p-dropdown-item:hover {
            background-color: var(--surface-50);
            color: var(--surface-900);
          }

          .sidebar-collapse-toggle {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            border: none;
            border-radius: 6px;
            background: var(--surface-100);
            color: var(--surface-600);
            cursor: pointer;
            flex-shrink: 0;
            transition: background-color 0.2s ease, color 0.2s ease;
          }

          .sidebar-collapse-toggle:hover {
            background: var(--surface-200);
            color: var(--surface-900);
          }

          .sidebar-collapsed .sidebar-collapse-toggle {
            margin: 0 auto;
          }

          .collapsed-menu {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            padding-top: 8px;
          }

          .collapsed-icon-item {
            width: 44px;
            height: 44px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
          }

          .collapsed-icon-item i {
            font-size: 1.1rem;
            margin: 0 !important;
          }

          .flyout-item {
            height: 40px;
            border-radius: 4px;
          }

          .sub-flyout-item {
            padding-right: 24px;
          }

          .flyout-group {
            margin-bottom: 8px;
          }

          .flyout-group:last-child {
            margin-bottom: 0;
          }

          .flyout-group-header {
            display: flex;
            align-items: center;
            padding: 8px 12px 4px;
            font-weight: 600;
            font-size: 0.85rem;
            color: var(--surface-700);
          }

          .sidebar-flyout-panel .p-overlaypanel-content {
            padding: 8px;
          }
        `}
      </style>
    </div>
  );
};

export default observer(SideBarAdministration);
